type BootstrapApiEndpoints = Record<string, string>

export type BootstrapPayload = {
    api?: BootstrapApiEndpoints
    space?: { id?: string | null } | null
    currentUser?: {
        space_id?: string | null
        signup_review_status?: string | null
    } | null
    KNOVIGATOR_SPACE_ID?: string | null
    authStateTrusted?: boolean
    [key: string]: unknown
}

/**
 * Pure bootstrap payload helpers used by app startup and regression tests.
 */

export function prefixBootstrapApiEndpoints(
    apiEndpoints: BootstrapApiEndpoints = {},
    apiOrigin = '',
): BootstrapApiEndpoints {
    return Object.fromEntries(
        Object.entries(apiEndpoints).map(([key, value]) => [
            key,
            typeof value === 'string' && /^https?:\/\//.test(value)
                ? value
                : `${apiOrigin}${value}`,
        ]),
    )
}

export function normalizeBootstrapPayload(
    bootstrapPayload: BootstrapPayload | null | undefined,
    apiOrigin = '',
): BootstrapPayload | null {
    if (!bootstrapPayload || typeof bootstrapPayload !== 'object') {
        return null
    }

    return {
        ...bootstrapPayload,
        api: prefixBootstrapApiEndpoints(bootstrapPayload.api, apiOrigin),
    }
}

export function getBootstrapSpaceId(
    bootstrapPayload: BootstrapPayload | null = null,
    defaultSpace: string | null = null,
): string | null {
    return (
        bootstrapPayload?.space?.id ||
        bootstrapPayload?.currentUser?.space_id ||
        bootstrapPayload?.KNOVIGATOR_SPACE_ID ||
        defaultSpace ||
        null
    )
}

export function isInlineBootstrapAuthTrusted(
    bootstrapPayload: BootstrapPayload | null | undefined,
): boolean {
    return bootstrapPayload?.authStateTrusted === true
}

export function getBootstrapLoadSource(
    bootstrapPayload: BootstrapPayload | null | undefined,
): 'network' | 'inline-trusted' | 'inline-public-revalidated' {
    if (!bootstrapPayload) {
        return 'network'
    }

    if (isInlineBootstrapAuthTrusted(bootstrapPayload)) {
        return 'inline-trusted'
    }

    return 'inline-public-revalidated'
}

export function shouldReuseInlineBootstrapAuth({
    cache = true,
    inlineBootstrap = null,
}: {
    cache?: boolean
    inlineBootstrap?: BootstrapPayload | null
} = {}): boolean {
    return Boolean(cache && inlineBootstrap && isInlineBootstrapAuthTrusted(inlineBootstrap))
}
